import React from 'react';
import { LogEntry } from '../src/types';

interface LogPanelProps {
  logs: LogEntry[];
  filterAudience?: 'user' | 'developer';
}

const LogPanel: React.FC<LogPanelProps> = ({ logs, filterAudience }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Filter logs based on audience rules
  const filteredLogs = React.useMemo(() => {
    if (!filterAudience) return logs;
    return logs.filter(log => {
      if (filterAudience === 'user') {
        // Show only explicit user logs
        return log.audience === 'user';
      } else {
        // Show explicit developer logs OR undefined logs
        return log.audience === 'developer' || log.audience === undefined || !log.audience;
      }
    });
  }, [logs, filterAudience]);

  React.useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [filteredLogs]);

  return (
    <div className="bg-black border border-cv-border rounded-lg p-2 h-48 flex flex-col font-mono text-xs">
      <div className="flex justify-between items-center px-2 py-1 border-b border-cv-border mb-1">
         <span className="text-cv-muted font-bold uppercase">
           {filterAudience === 'user' ? 'Progress Feed' : 'Advanced System Logs'}
         </span>
         <span className="text-cv-disabled">{filteredLogs.length} events</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-1 p-2">
        {filteredLogs.map((log) => {
          // If developer feed, prioritize technicalMessage over default message
          const displayMessage = (filterAudience === 'developer' && log.technicalMessage) 
            ? log.technicalMessage 
            : log.message;

          return (
            <div key={log.id} className="flex space-x-2 hover:bg-cv-panel p-0.5 rounded">
              <span className="text-cv-disabled flex-shrink-0">[{log.timestamp}]</span>
              <span className={`font-bold flex-shrink-0 w-24 truncate ${
                log.agent === 'System' ? 'text-cv-muted' :
                log.agent === 'Scribe' ? 'text-cv-interactive' :
                log.agent === 'Archivist' ? 'text-green-400' :
                'text-cv-danger'
              }`}>
                {log.agent}
              </span>
              <span className={`break-words flex-1 ${
                log.type === 'error' ? 'text-cv-danger font-semibold' :
                log.type === 'success' ? 'text-green-500' :
                log.type === 'warning' ? 'text-yellow-500' :
                'text-cv-secondary'
              }`}>
                {displayMessage}
                {log.requestId && filterAudience === 'developer' && (
                  <span className="text-[10px] text-cv-disabled ml-2 font-light">
                    (ID: {log.requestId})
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LogPanel;